import { useState } from 'react';
import { motion } from 'framer-motion';
import { QuizQuestion } from '@shared/types';

interface ScaleInputProps {
  question: QuizQuestion;
  value: number;
  onChange: (value: number) => void;
  onAutoAdvance?: (value: number) => void;
}

export default function ScaleInput({ question, value, onChange, onAutoAdvance }: ScaleInputProps) {
  const handleSelect = (scaleValue: number) => {
    onChange(scaleValue);
    if (onAutoAdvance) {
      onAutoAdvance(scaleValue);
    }
  };
  const [hoveredValue, setHoveredValue] = useState<number | null>(null);
  
  // Generate scale array dynamically based on question's min/max
  const scaleMin = question.scaleMin ?? 1;
  const scaleMax = question.scaleMax ?? 7;
  const scale = Array.from({ length: scaleMax - scaleMin + 1 }, (_, i) => scaleMin + i);
  
  // Get labels from question or use defaults
  const minLabel = question.scaleLabels?.min;
  const maxLabel = question.scaleLabels?.max;

  return (
    <div className="space-y-6">
      {/* Show labels if provided */}
      {(minLabel || maxLabel) && (
        <div className="flex justify-between items-center">
          <span className="text-sm text-dark-muted">{minLabel || scaleMin}</span>
          <span className="text-sm text-dark-muted">{maxLabel || scaleMax}</span>
        </div>
      )}

      <div className="flex justify-between items-center gap-2">
        {scale.map((num) => {
          const isSelected = value === num;
          const isHovered = hoveredValue === num;
          
          return (
            <button
              key={num}
              onClick={() => handleSelect(num)}
              onMouseEnter={() => setHoveredValue(num)}
              onMouseLeave={() => setHoveredValue(null)}
              className="relative flex-1 text-center"
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className={`
                  h-16 max-w-16 mx-auto rounded-xl flex items-center justify-center self-center text-lg font-bold transition-all
                  ${isSelected || isHovered
                    ? 'bg-primary-700 text-white shadow-lg'
                    : 'bg-dark-elevated text-dark-muted hover:bg-dark-border'
                  }
                `}
              >
                {num}
              </motion.div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

